import type { Currency, FinancialDataQueryParams, Unit } from '../../types/api';
import { convertUSDToCurrency } from './currencyConversion';

/**
 * Shared building blocks for reading per-unit financial statements.
 * Balance sheet, income statement and cashflow rows all share the same
 * category/date/value shape, so portfolio and per-property calculations
 * can rely on the same primitives.
 */
export interface StatementRow {
  calendar_date: string;
  category_code: number;
  category_value?: number;
  portioned_value?: number;
}

export type UnitStatementRow<T> = T & { unit_id_no: number };

/** Statement dates arrive either as `YYYYMM` or as a parsable date string. */
export function parseStatementDate(dateStr: string): Date {
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    return new Date(year, month, 1);
  }

  return new Date(dateStr);
}

/** Twelve-month window ending on the reference date, start exclusive so it spans 12 months. */
export function isWithinTtm(date: Date, referenceDate = new Date()): boolean {
  const start = new Date(referenceDate);
  start.setMonth(start.getMonth() - 12);
  return date > start && date <= referenceDate;
}

export type PeriodMode = 'ttm' | 'ytd' | 'all';

export interface PeriodWindow {
  mode: PeriodMode;
  /** Inclusive start for YTD. Exclusive start for TTM. Null when the window is unbounded. */
  start: Date | null;
  /** Inclusive end, anchored on the latest reported month when one is known. */
  end: Date;
  /** Raw statement date the window ends on, when the anchor came from reported data. */
  endDateKey: string | null;
}

export const PERIOD_MODE_OPTIONS: ReadonlyArray<{ id: PeriodMode; label: string }> = [
  { id: 'ttm', label: 'TTM' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all', label: 'All Time' },
];

export function periodModeLabel(mode: PeriodMode): string {
  return PERIOD_MODE_OPTIONS.find((option) => option.id === mode)?.label ?? 'TTM';
}

/** Latest raw statement date among the keys that actually exist. */
export function latestStatementDateKey(
  dateKeys: Array<string | null | undefined>
): string | null {
  return dateKeys.reduce<string | null>((latest, dateKey) => {
    if (!dateKey) return latest;
    if (!latest) return dateKey;

    const dateTime = parseStatementDate(dateKey).getTime();
    const latestTime = parseStatementDate(latest).getTime();
    if (Number.isNaN(dateTime)) return latest;
    if (Number.isNaN(latestTime) || dateTime > latestTime) return dateKey;

    return latest;
  }, null);
}

/**
 * TTM matches `isWithinTtm`. YTD starts on 1 January of the anchor year.
 * All Time includes every month through the anchor.
 */
export function resolvePeriodWindow(mode: PeriodMode, anchorDateKey: string | null): PeriodWindow {
  const end = anchorDateKey ? parseStatementDate(anchorDateKey) : new Date();

  if (mode === 'ytd') {
    return {
      mode,
      start: new Date(end.getFullYear(), 0, 1),
      end,
      endDateKey: anchorDateKey,
    };
  }

  if (mode === 'ttm') {
    const start = new Date(end);
    start.setMonth(start.getMonth() - 12);
    return { mode, start, end, endDateKey: anchorDateKey };
  }

  return { mode, start: null, end, endDateKey: anchorDateKey };
}

export function isWithinPeriodWindow(date: Date, periodWindow: PeriodWindow): boolean {
  if (Number.isNaN(date.getTime()) || date > periodWindow.end) {
    return false;
  }

  if (periodWindow.mode === 'ttm') {
    return isWithinTtm(date, periodWindow.end);
  }

  if (periodWindow.start === null) {
    return true;
  }

  return date >= periodWindow.start;
}

/** Mid-sentence description, for example `the 12 months to Oct 2024`. */
export function describePeriodWindow(periodWindow: PeriodWindow): string {
  const endLabel = periodWindow.endDateKey
    ? formatStatementMonthLabel(periodWindow.endDateKey)
    : '';

  if (periodWindow.mode === 'ttm') {
    return endLabel ? `the 12 months to ${endLabel}` : 'the trailing twelve months';
  }

  if (periodWindow.mode === 'ytd') {
    return endLabel ? `year to date through ${endLabel}` : 'year to date';
  }

  return endLabel ? `all time through ${endLabel}` : 'all time';
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Formats from the raw statement date so timezone offsets cannot shift the month. */
export function formatStatementMonthLabel(dateStr: string): string {
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const monthIndex = parseInt(dateStr.substring(4, 6), 10) - 1;
    return `${MONTH_LABELS[monthIndex] || ''} ${dateStr.substring(0, 4)}`.trim();
  }

  const [year, month] = dateStr.split('-');
  const monthIndex = parseInt(month, 10) - 1;

  return `${MONTH_LABELS[monthIndex] || ''} ${year}`.trim();
}

/** Parses chart axis labels such as `Jan '24` into the first of that month. */
export function parseChartMonthLabel(label: string): Date | null {
  const match = /^([A-Za-z]{3}) '(\d{2})$/.exec(label.trim());
  if (!match) return null;

  const monthIndex = MONTH_LABELS.indexOf(match[1]);
  if (monthIndex < 0) return null;

  const yearShort = parseInt(match[2], 10);
  const year = yearShort < 50 ? 2000 + yearShort : 1900 + yearShort;
  return new Date(year, monthIndex, 1);
}

/** `YYYYMM` key so a chart month can anchor `resolvePeriodWindow`. */
export function statementDateKeyFromDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}${month}`;
}

/**
 * Most recent month that actually reported a value for any of the given
 * categories. Datasets often carry zero-filled rows up to the present, so
 * trailing-twelve-month windows anchor here rather than on today's date.
 */
export function findLatestReportedDate(
  rows: StatementRow[],
  categoryCodes: number[]
): string | null {
  return rows.reduce<string | null>((latest, row) => {
    if (!categoryCodes.includes(row.category_code)) return latest;
    if (getStatementRowValue(row) === 0) return latest;
    if (latest && row.calendar_date <= latest) return latest;

    return row.calendar_date;
  }, null);
}

/** Ownership-adjusted value when the statement provides one, raw value otherwise. */
export function getStatementRowValue(row: StatementRow): number {
  return row.portioned_value ?? row.category_value ?? 0;
}

/**
 * Statement values are stored in each unit's own currency, so conversion
 * routes through USD before reaching the requested display currency.
 */
export function convertUnitValueToTargetCurrency(
  value: number,
  unitId: number,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): number {
  const unit = allUnits.find((entry) => entry.unit_id_no === unitId);
  const sourceCurrency = unit?.currency || 'USD';

  if (!targetCurrency || sourceCurrency === targetCurrency) {
    return value;
  }

  const sourceRate = currencies.find((entry) => entry.currency === sourceCurrency);
  const sourceUnitsPerUsd = sourceRate?.units_per_usd || 1;
  const valueInUsd = value / sourceUnitsPerUsd;

  return convertUSDToCurrency(valueInUsd, targetCurrency, currencies);
}

/**
 * Requests one statement type for every client unit in parallel and tags each
 * row with its unit so results can be grouped afterwards. Units that fail are
 * skipped so a single bad response cannot blank the whole table.
 */
export async function fetchStatementsForUnits<T extends { unit_id_no: number }>(
  userId: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  fetchFn: (params: FinancialDataQueryParams) => Promise<T[]>
): Promise<Array<UnitStatementRow<T>>> {
  const results = await Promise.all(
    clientUnits.map(async (unit) => {
      if (!unit.unit_id_no) return null;

      try {
        const data = await fetchFn({
          user_id_no: userId,
          unit_id_no: unit.unit_id_no,
        });

        return data.map((item) => ({ ...item, unit_id_no: unit.unit_id_no! }));
      } catch (error) {
        console.warn(`Failed to fetch financial data for unit ${unit.unit_id_no}:`, error);
        return null;
      }
    })
  );

  return results
    .filter((result): result is Array<UnitStatementRow<T>> => result !== null)
    .flat();
}

export function groupStatementRowsByUnit<T extends { unit_id_no: number }>(
  rows: T[]
): Map<number, T[]> {
  return rows.reduce((grouped, row) => {
    const existing = grouped.get(row.unit_id_no);

    if (existing) {
      existing.push(row);
    } else {
      grouped.set(row.unit_id_no, [row]);
    }

    return grouped;
  }, new Map<number, T[]>());
}

/** Point-in-time reading, such as an equity valuation on the balance sheet. */
export function sumLatestCategoryValue(rows: StatementRow[], categoryCode: number): number {
  const categoryRows = rows.filter((row) => row.category_code === categoryCode);

  if (categoryRows.length === 0) {
    return 0;
  }

  const latestDate = categoryRows.reduce(
    (latest, row) => (row.calendar_date > latest ? row.calendar_date : latest),
    categoryRows[0].calendar_date
  );

  return categoryRows
    .filter((row) => row.calendar_date === latestDate)
    .reduce((total, row) => total + getStatementRowValue(row), 0);
}

/** Flow reading over the trailing twelve months, such as NOI or net cash. */
export function sumTtmCategoryValue(
  rows: StatementRow[],
  categoryCode: number,
  referenceDate = new Date()
): number {
  return rows.reduce((total, row) => {
    if (row.category_code !== categoryCode) {
      return total;
    }

    if (!isWithinTtm(parseStatementDate(row.calendar_date), referenceDate)) {
      return total;
    }

    return total + getStatementRowValue(row);
  }, 0);
}

function compareStatementDates(left: string, right: string): number {
  const leftTime = parseStatementDate(left).getTime();
  const rightTime = parseStatementDate(right).getTime();

  if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
    return left.localeCompare(right);
  }

  return leftTime - rightTime;
}

/** Flow reading summed across the months inside the window. */
export function sumCategoryValueInWindow(
  rows: StatementRow[],
  categoryCode: number,
  periodWindow: PeriodWindow
): number {
  return rows.reduce((total, row) => {
    if (row.category_code !== categoryCode) {
      return total;
    }

    if (!isWithinPeriodWindow(parseStatementDate(row.calendar_date), periodWindow)) {
      return total;
    }

    return total + getStatementRowValue(row);
  }, 0);
}

/** Point-in-time reading on the latest month that falls inside the window. */
export function sumCategoryValueAtWindowEnd(
  rows: StatementRow[],
  categoryCode: number,
  periodWindow: PeriodWindow
): number {
  const categoryRows = rows.filter(
    (row) =>
      row.category_code === categoryCode &&
      isWithinPeriodWindow(parseStatementDate(row.calendar_date), periodWindow)
  );

  if (categoryRows.length === 0) {
    return 0;
  }

  const latestDate = categoryRows.reduce(
    (latest, row) => (compareStatementDates(row.calendar_date, latest) > 0 ? row.calendar_date : latest),
    categoryRows[0].calendar_date
  );

  return categoryRows
    .filter((row) => row.calendar_date === latestDate)
    .reduce((total, row) => total + getStatementRowValue(row), 0);
}

export function hasCategoryData(rows: StatementRow[], categoryCode: number): boolean {
  return rows.some((row) => row.category_code === categoryCode);
}
